import { merge } from 'lodash-es';
import { createTierDataConfig, TierDataConfig, TierDataFormulas } from '../schemas/FundWaterfall.schema';

export type TierOrderNumber = `${number}`;

export const PER_CLASS_PROCEEDS_KEYS = ['lpTotalProceeds', 'slpTotalProceeds', 'gpTotalProceeds'] as const;
export type PerClassProceedsKey = (typeof PER_CLASS_PROCEEDS_KEYS)[number];

export const PER_CLASS_PROCEEDS_LABELS: Record<PerClassProceedsKey, string> = {
  lpTotalProceeds: 'LP Total Proceeds',
  slpTotalProceeds: 'SLP Total Proceeds',
  gpTotalProceeds: 'GP Total Proceeds',
};

export type WaterfallConfigPayload = {
  fundId: number;
  totalProceeds: string | null;
  lpTotalProceeds?: string | null;
  slpTotalProceeds?: string | null;
  gpTotalProceeds?: string | null;
  // BE assumes totalProceeds is orderNumber 0, so tiers should start at 1
  tiers: { [orderNumber: number]: TierDataConfig };
};

export type WaterfallTierConfig = TierDataConfig & {
  fundId: number;
  tierId: string;
};

export function createWaterfallTierConfig(overrides: Partial<WaterfallTierConfig> = {}): WaterfallTierConfig {
  return merge(
    {
      fundId: 0,
      tierId: '0',
      ...createTierDataConfig(),
    },
    overrides
  );
}

export type WaterfallConfig = {
  totalProceeds: string | null;
  lpTotalProceeds?: string | null;
  slpTotalProceeds?: string | null;
  gpTotalProceeds?: string | null;
  tiers: { [orderNumber: number]: WaterfallTierConfig };
};
export function createWaterfallConfig(overrides: Partial<WaterfallConfig> = {}): WaterfallConfig {
  return merge(
    {
      totalProceeds: null,
      tiers: {},
    },
    overrides
  );
}

export type TierResult = { [key in keyof TierDataFormulas]: number | null } & {
  remaining?: number | null;
  total?: number | null;
  lpTotal?: number | null;
  lpRemaining?: number | null;
  slpTotal?: number | null;
  slpRemaining?: number | null;
  gpTotal?: number | null;
  gpRemaining?: number | null;
};

export function createTierResult(overrides: Partial<TierResult> = {}): TierResult {
  return merge(
    {
      carry: null,
      gp: null,
      lp: null,
      slp: null,
      remaining: null,
      total: null,
    },
    overrides
  );
}

export type WaterfallResult = {
  gpNav?: number | null;
  lpNav?: number | null;
  slpNav?: number | null;
  totalProceeds: number;
  lpTotalProceeds?: number | null;
  slpTotalProceeds?: number | null;
  gpTotalProceeds?: number | null;
  tiers: {
    [orderNumber: number]: TierResult;
  };
};

export function createWaterfallResult(overrides: Partial<WaterfallResult> = {}): WaterfallResult {
  return merge(
    {
      gpNav: null,
      lpNav: null,
      slpNav: null,
      totalProceeds: 0,
      tiers: {},
    },
    overrides
  );
}

export type FundParametersResult = {
  basicParameters: Record<string, number | null>;
  customParameters: Record<string, number | null>;
};

export type FundWaterfallConfigResponse = {
  fundId: number;
  configuration: WaterfallConfig;
};

export function createFundWaterfallConfigResponse(
  overrides: Partial<FundWaterfallConfigResponse> = {}
): FundWaterfallConfigResponse {
  return merge(
    {
      fundId: 0,
      configuration: createWaterfallConfig(),
    },
    { ...overrides }
  );
}

export type UpdateWaterfallConfigResponse = {
  fundId: number;
  configuration: WaterfallConfig;
  validationMessages:
    | Record<'totalProceeds', string>
    | Record<'tiers', Record<TierOrderNumber, Record<'formulas', Record<keyof TierDataFormulas, string>>>>;
};

/* 
	"validationMessages": { // this is optional on responses
		"totalProceeds": string,
		"tiers": {
			"tier_order_number_as_string": { 
				"formulas": {
					"lp": string,
					"gp": string,
					"slp": string,
					"carry": string,
				}
			},...
		}
*/

export type FundWaterfallResponse = {
  fundId: number;
  parameters: FundParametersResult;
  configuration: WaterfallConfig;
  results: WaterfallResult | null;
  message: string | null;
};

export function createFundWaterfallResponse(
  overrides: Partial<FundWaterfallResponse> = {}
): FundWaterfallResponse {
  return merge(
    {
      fundId: -1,
      parameters: {},
      configuration: createWaterfallConfig(),
      results: null,
      message: null,
    },
    overrides
  );
}

export type FundWaterfallResultsResponse = {
  fundId: number;
  results: WaterfallResult | null;
  message: string | null;
};

export type FormulaValidationResponse =
  | { isValidFormula: false; message: string }
  | { isValidFormula: true; message?: null };
